import { endOfMonth } from 'date-fns';
import {
  DateAfter,
  DateBefore,
  DateInterval,
  DayOfWeek,
  Matcher,
} from 'react-day-picker';
import { NavDirection } from './NavButton.types';

const WEEKDAY_COUNT = 7;

export const isAnotherMonthDisabled = (
  anotherMonth: Date,
  disabledDates: Matcher | Matcher[] | undefined,
  navDirection: NavDirection,
): boolean => {
  if (disabledDates === undefined) {
    return false;
  }

  if (Array.isArray(disabledDates)) {
    return disabledDates.every((datesOrMatcher) =>
      isNavButtonDisabled(anotherMonth, datesOrMatcher, navDirection),
    );
  }

  return isNavButtonDisabled(anotherMonth, disabledDates, navDirection);
};

const isDayOfWeekDisabled = ({ dayOfWeek }: DayOfWeek): boolean => {
  if (!Array.isArray(dayOfWeek)) {
    return false;
  }

  const disabledWeekdays = dayOfWeek.sort((first, second) => first - second);

  return [...Array(WEEKDAY_COUNT).keys()].every(
    (weekdayIndex, index) => weekdayIndex === disabledWeekdays[index],
  );
};

const isIntervalDisabled = (
  month: Date,
  disabledDates: DateBefore | DateAfter | DateInterval,
  navDirection: NavDirection,
): boolean => {
  if (navDirection === 'previous') {
    return (
      'before' in disabledDates && endOfMonth(month) < disabledDates.before
    );
  }

  return (
    navDirection === 'next' &&
    'after' in disabledDates &&
    endOfMonth(month) > disabledDates.after
  );
};

export const isNavButtonDisabled = (
  month: Date,
  disabledDates: Matcher,
  navDirection: NavDirection,
): boolean => {
  if (typeof disabledDates === 'boolean') {
    return disabledDates;
  }

  if (
    disabledDates instanceof Function ||
    disabledDates instanceof Date ||
    Array.isArray(disabledDates)
  ) {
    return false;
  }

  if ('dayOfWeek' in disabledDates) {
    return isDayOfWeekDisabled(disabledDates);
  }

  if ('from' in disabledDates || 'to' in disabledDates) {
    return false;
  }

  if ('before' in disabledDates || 'after' in disabledDates) {
    return isIntervalDisabled(month, disabledDates, navDirection);
  }

  return false;
};
